// FILE: TeamsSection.logic.ts
// Purpose: Pure helpers behind the Teams settings panel — sorting, member name
//          lookup, and name validation — kept apart from the component so they
//          can be unit tested without mounting React.
// Exports: `sortTeamsByName`, `resolveMemberNames`, `validateTeamName`.

import { TEAM_NAME_MAX_LENGTH, type TeamWithMembers, type UserId } from "@taskin-code/contracts";

/** Alphabetical by name, case-insensitive — matches how an admin scans a list looking for one team. */
export function sortTeamsByName(
  teams: ReadonlyArray<TeamWithMembers>,
): ReadonlyArray<TeamWithMembers> {
  return teams.toSorted((left, right) =>
    left.name.localeCompare(right.name, undefined, { sensitivity: "base" }),
  );
}

export interface UserNameLookup {
  readonly userId: UserId;
  readonly name: string;
}

/**
 * Member ids -> display names, in the same order as `memberIds`.
 *
 * A member whose account is missing from the admin user list (deleted, or the
 * list has not loaded yet) still needs a row — dropping it would hide a
 * membership grant nobody could then find to remove.
 */
export function resolveMemberNames(
  memberIds: ReadonlyArray<UserId>,
  users: ReadonlyArray<UserNameLookup>,
): ReadonlyArray<string> {
  const byId = new Map(users.map((user) => [user.userId, user.name]));
  return memberIds.map((id) => byId.get(id) ?? "Unknown user");
}

export interface TeamNameValidation {
  readonly ok: boolean;
  readonly error?: string;
}

/** Same rule the server enforces (non-empty, <= TEAM_NAME_MAX_LENGTH) so the dialog can fail fast before the round trip. */
export function validateTeamName(name: string): TeamNameValidation {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: "Team name is required." };
  }
  if (trimmed.length > TEAM_NAME_MAX_LENGTH) {
    return { ok: false, error: `Team name must be ${TEAM_NAME_MAX_LENGTH} characters or fewer.` };
  }
  return { ok: true };
}
